/// <amd-module name="Wit/WorkItemForm/History/Diffs/LinkDiff" />
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { usePageContext } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { WorkItemRelation } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import { ArtifactLink } from "Wit/WorkItemForm/Links";
import * as Resources from "Wit/WorkItemForm/Resources";
import { ILinkDisplayData, ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { resolveArtifact } from "Wit/WorkItemLinksRegistration/resolveArtifacts";

export interface ILinkUpdateProps {
    link: WorkItemRelation;
    historyLinksOptimizationsEnabled?: boolean
    matchingLinkIdentifier?: ILinkIdentifier;
    resolvedArtifact?: IResolvedLinkDisplayData;
    changeType: "added" | "removed" | "updated";
}

export function LinkDiff({ link, changeType, matchingLinkIdentifier, resolvedArtifact, historyLinksOptimizationsEnabled }: ILinkUpdateProps) {
    const pageContext = usePageContext();
    const workItem = useWorkItem();
    const [loading, setLoading] = useState(true);
    const [linkDetails, setLinkDetails] = useState<ILinkDisplayData | undefined>();

    const refreshLinkInfo = useCallback(async () => {
        setLoading(true);

        let matchingLinkIdentifierToUse = matchingLinkIdentifier;

        if (!historyLinksOptimizationsEnabled) {
            const linkIdentifiers = await pageContext
                .getService<IWorkItemLinksService>("IWorkItemLinksService")
                .getLinkIdentifiers(workItem, { includeDeleted: true });

            matchingLinkIdentifierToUse = linkIdentifiers.find(l => {
                return link.url === l.FilePath || link.url.endsWith(`/_apis/wit/workItems/${l.ID}`);
            });
        }

        if (!matchingLinkIdentifierToUse) {
            setLinkDetails(undefined);
            setLoading(false);
            return;
        }

        if (historyLinksOptimizationsEnabled && resolvedArtifact) {
            setLinkDetails(resolvedArtifact);
            setLoading(false);
            return;
        }

        const matchingLinkDetails = await resolveArtifact(matchingLinkIdentifierToUse, workItem.id, pageContext);
        setLinkDetails(matchingLinkDetails);
        setLoading(false);
    }, [link, matchingLinkIdentifier]);

    useEffect(() => {
        refreshLinkInfo();
    }, [refreshLinkInfo]);

    const content = (
        <div className="padding-vertical-4 margin-bottom-8 flex-row flex-center history-test-step-link">
            <Icon iconName="Link" className="icon-margin" ariaHidden />
            <span className="margin-right-16">{link.attributes?.name ?? Resources.Link}</span>
            <div className={`${changeType} padding-vertical-4 padding-horizontal-8`}>
                {loading ? (
                    <div className="link-diff-details-placeholder shimmer shimmer-line" />
                ) : linkDetails ? (
                    <ArtifactLink artifactData={linkDetails} />
                ) : (
                    <a
                        className="error-text"
                        href="#"
                        onClick={e => {
                            e.preventDefault();
                            refreshLinkInfo();
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                refreshLinkInfo();
                            }
                        }}
                        tabIndex={0}
                    >
                        {Resources.HistoryLinkDiffErrorLoadingDetails}
                    </a>
                )}
            </div>
        </div>
    );

    return changeType === "added" ? <ins>{content}</ins> : changeType === "removed" ? <del>{content}</del> : <div>{content}</div>;
}
